import { Search, Zap, Shield, Users } from "lucide-react";

export const LeftPanel = () => {
  return (
    <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 to-gray-800 text-white p-12 relative overflow-hidden">
      {/* Gradient Mesh Background */}
      <div className="absolute inset-0">
        {/* Base gradient - darker */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950" />

        {/* Animated Mesh gradients - increased opacity */}
        <div className="absolute top-0 -left-40 w-96 h-96 bg-[#01B9D9] rounded-full mix-blend-screen opacity-40 blur-[120px] animate-float" />
        <div className="absolute top-1/3 -right-20 w-80 h-80 bg-[#F6C768] rounded-full mix-blend-screen opacity-35 blur-[100px] animate-float-delayed" />
        <div className="absolute -bottom-32 left-1/4 w-96 h-96 bg-[#EB7138] rounded-full mix-blend-screen opacity-30 blur-[120px] animate-float-slow" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#01B9D9] rounded-full mix-blend-screen opacity-15 blur-[150px] animate-pulse-slow" />

        {/* Additional animated gradients for depth */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-br from-[#01B9D9]/30 to-transparent rounded-full blur-[80px] animate-float-reverse" />
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-tl from-[#F6C768]/30 to-transparent rounded-full blur-[60px] animate-float-delayed-reverse" />

        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.025] mix-blend-overlay">
          <svg width="100%" height="100%">
            <filter id="noiseFilter">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.85"
                numOctaves="1"
                result="turbulence"
              />
              <feColorMatrix in="turbulence" type="saturate" values="0" />
            </filter>
            <rect width="100%" height="100%" filter="url(#noiseFilter)" />
          </svg>
        </div>
      </div>

      <div className="relative z-10 flex flex-col justify-between w-full max-w-lg mx-auto">
        <div>
          <a href="https://specstory.com/cloud" className="inline-block mb-8">
            <img
              src="/specstorylogo-white.png"
              alt="SpecStory"
              className="h-8 w-auto"
            />
          </a>

          <h1 className="text-4xl font-bold mb-4 leading-tight text-white">
            Your AI conversations deserve a better home.
          </h1>

          <p className="text-lg text-gray-300 mb-8 leading-relaxed">
            Join thousands of developers who are building their AI memory with
            SpecStory.
          </p>

          <div className="space-y-3">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 transition-all duration-300 hover:bg-white/10 hover:border-white/20 group">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Search className="w-5 h-5 text-white/80" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Instant Search</h3>
                  <p className="text-sm text-gray-400">
                    Find any conversation in milliseconds
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 transition-all duration-300 hover:bg-white/10 hover:border-white/20 group">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white/80" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Automatic Sync</h3>
                  <p className="text-sm text-gray-400">
                    Seamlessly sync from all your AI tools
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 transition-all duration-300 hover:bg-white/10 hover:border-white/20 group">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white/80" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Privacy First</h3>
                  <p className="text-sm text-gray-400">
                    Your data stays yours, always
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 transition-all duration-300 hover:bg-white/10 hover:border-white/20 group">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white/80" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Team Knowledge</h3>
                  <p className="text-sm text-gray-400">
                    Build collective AI memory
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <p className="text-sm text-gray-400 mb-3">Works seamlessly with</p>
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 p-2 transition-all duration-300 hover:bg-white/20 hover:scale-110">
              <img
                src="/assets/cursor.svg"
                alt="Cursor"
                className="w-full h-full filter brightness-0 invert opacity-80"
              />
            </div>
            <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 p-2 transition-all duration-300 hover:bg-white/20 hover:scale-110">
              <img
                src="/assets/vscode.svg"
                alt="VS Code"
                className="w-full h-full filter brightness-0 invert opacity-80"
              />
            </div>
            <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 p-2 transition-all duration-300 hover:bg-white/20 hover:scale-110">
              <img
                src="/assets/claude.svg"
                alt="Claude"
                className="w-full h-full filter brightness-0 invert opacity-80"
              />
            </div>
            <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 p-2 transition-all duration-300 hover:bg-white/20 hover:scale-110">
              <img
                src="/assets/githubcopilot.svg"
                alt="GitHub Copilot"
                className="w-full h-full filter brightness-0 invert opacity-80"
              />
            </div>
            <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 p-2 transition-all duration-300 hover:bg-white/20 hover:scale-110">
              <img
                src="/assets/openai.svg"
                alt="OpenAI"
                className="w-full h-full filter brightness-0 invert opacity-80"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
